import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import copyJson from '../../demo/fixtures/w3/copy.json'
import copyW4 from '../../demo/fixtures/w4/copy.json'
import { selectAccountUplift, selectTrackedCampaigns } from '../../demo/selectors'
import { demoProducts, useDemoStore } from '../../demo/store'
import type { AccountUplift } from '../../demo/types'
import { Button, Card, Icon, Spinner, Toast, ToastHost } from '../../ui'
import { formatUpliftPercent } from './overview'
import styles from './AutopilotOverviewCard.module.css'

const productsById = new Map(demoProducts.map((product) => [product.id, product]))

function renderReportLine(template: string, uplift: AccountUplift) {
  return template.split(/(\{total\}|\{n\}|\{ctr\})/).map((part, index) => {
    if (part === '{total}') return String(uplift.total)
    if (part === '{n}') return <b className={styles.hiAi} key={`tracked-${index}`}>{uplift.tracked}</b>
    if (part === '{ctr}') return <b className={styles.hiUp} key={`ctr-${index}`}>{formatUpliftPercent(uplift.ctrDelta)}</b>
    return part
  })
}

export function AutopilotOverviewCard() {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const busyRef = useRef(false)
  const timerRef = useRef<number | null>(null)
  const campaigns = useDemoStore((state) => state.campaigns)
  const autopilotByCampaign = useDemoStore((state) => state.autopilotByCampaign)
  const optimizations = useDemoStore((state) => state.optimizations)
  const runCheckAll = useDemoStore((state) => state.runCheckAll)

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
  }, [])

  const uplift = useMemo(() => {
    void campaigns
    void autopilotByCampaign
    void optimizations
    return selectAccountUplift()
  }, [campaigns, autopilotByCampaign, optimizations])

  const trackedCampaigns = useMemo(() => {
    void campaigns
    void autopilotByCampaign
    void optimizations
    return selectTrackedCampaigns()
  }, [campaigns, autopilotByCampaign, optimizations])

  const openCampaign = (campaignId: string) => {
    void navigate(`/monitor/${campaignId}?focus=autopilot`)
  }

  const openFromKeyboard = (event: KeyboardEvent<HTMLDivElement>, campaignId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openCampaign(campaignId)
    }
  }

  const checkAll = () => {
    if (busyRef.current || uplift.tracked === 0) return
    busyRef.current = true
    setBusy(true)
    timerRef.current = window.setTimeout(() => {
      const count = runCheckAll()
      busyRef.current = false
      timerRef.current = null
      setBusy(false)
      setToast(copyJson.batchToast.replace('{n}', String(count)))
    }, 2000)
  }

  return (
    <Card data-scope="ap-ov-card" className={styles.card}>
      <div className={styles.top}>
        <span className={styles.avatar}>
          <Icon name="sparkle" size={24} strokeWidth={1.6} />
        </span>
        <div className={styles.topBody}>
          <p className={styles.title}>{copyW4.ovReport.title}</p>
          {uplift.tracked > 0 ? (
            <p className={styles.line}>{renderReportLine(copyW4.ovReport.line, uplift)}</p>
          ) : null}
        </div>
        <Button className={styles.checkButton} variant="secondary" size="pill-sm" disabled={busy || uplift.tracked === 0} onClick={checkAll}>
          {busy ? <Spinner className={styles.spinner} /> : <Icon name="sparkle" size={15} />}
          {copyJson.overviewCheck}
        </Button>
      </div>
      {uplift.tracked === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>{copyW4.ovReport.empty.title}</p>
          <p className={styles.emptySub}>{copyW4.ovReport.empty.sub}</p>
          <Button className={styles.emptyLink} variant="link" onClick={() => { void navigate('/monitor') }}>
            {copyW4.ovReport.empty.link}
          </Button>
        </div>
      ) : (
        <div className={styles.rows}>
          {trackedCampaigns.map(({ campaign, runs, lastAction }) => {
            const product = productsById.get(campaign.productId)
            return (
              <div
                className={styles.row}
                key={campaign.id}
                role="button"
                tabIndex={0}
                aria-label={`查看 ${product?.name ?? ''} 的 AI 优化记录`}
                onClick={() => openCampaign(campaign.id)}
                onKeyDown={(event) => openFromKeyboard(event, campaign.id)}
              >
                <img className={styles.thumb} src={`/${product?.image ?? ''}`} alt={product?.name ?? ''} />
                <div className={styles.rowBody}>
                  <p className={styles.name}>{product?.name ?? ''}</p>
                  <p className={styles.action}>{lastAction}</p>
                </div>
                <span className={styles.runs}>{runs} 次</span>
              </div>
            )
          })}
          <p className={styles.week}>{copyW4.ovReport.week.replace('{n}', String(uplift.weekRuns))}</p>
        </div>
      )}
      {toast ? <ToastHost><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </Card>
  )
}
